import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { PilotContactForm } from "@/components/pilot";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/seo/SEO";
import { trackEvent } from "@/services/tracking/googleAnalytics";

// Design tokens
const colors = {
  primary: "hsl(var(--primary))",
  charcoal: "hsl(var(--foreground))",
  midGray: "hsl(var(--muted-foreground))",
  lightGray: "hsl(var(--muted))",
  success: "#10b981",
};

export const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const handleSuccess = (email: string) => {
    trackEvent("contact_form_submit", {
      category: "conversion",
      label: "pilot_program_application",
      location: "contact_page",
    });
    setSubmittedEmail(email);
    setIsSubmitted(true);
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  if (isSubmitted) {
    // Thank You View
    return (
      <div
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: colors.lightGray }}
      >
        <SEO
          title="Thank You for Contacting TerraMatrix"
          description="We've received your message and will be in touch soon to discuss how TerraMatrix can help with your IoT monitoring needs."
          canonical="/contact"
          noindex={true}
        />
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-4 py-3">
          <button
            onClick={handleBackToHome}
            className="flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-opacity"
            style={{ color: colors.primary }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </header>

        {/* Thank You Content */}
        <main className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="bg-white rounded-lg shadow-sm max-w-md w-full p-6 text-center">
            {/* Success Icon */}
            <div className="flex justify-center mb-4">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${colors.success}15` }}
              >
                <CheckCircle
                  className="w-10 h-10"
                  style={{ color: colors.success }}
                />
              </div>
            </div>

            <h1
              className="text-2xl font-semibold mb-2"
              style={{ color: colors.charcoal }}
            >
              Thank You for Your Interest!
            </h1>

            <p className="text-sm mb-6" style={{ color: colors.midGray }}>
              We've received your application to join the TerraMatrix Pilot Program.
            </p>

            {/* Next Steps */}
            <div
              className="bg-gray-50 rounded-lg p-4 text-sm text-left mb-6"
              style={{ color: colors.charcoal }}
            >
              <p className="font-medium mb-2">What happens next?</p>
              <ul className="list-disc list-inside space-y-1" style={{ color: colors.midGray }}>
                <li>Our team will review your application within 5-7 business days</li>
                <li>
                  We'll reach out to you at{" "}
                  <span className="font-medium" style={{ color: colors.charcoal }}>
                    {submittedEmail}
                  </span>{" "}
                  with next steps
                </li>
                <li>Selected partners will receive early access to TerraMatrix</li>
              </ul>
            </div>

            <Button
              onClick={handleBackToHome}
              className="w-full h-10 font-medium transition-all duration-200 hover:brightness-95"
              style={{
                backgroundColor: colors.primary,
                color: "#FFFFFF",
              }}
            >
              Back to Home
            </Button>

            <p className="text-xs mt-4" style={{ color: colors.midGray }}>
              Have questions? Reach out to{" "}
              <a
                href="mailto:pilot@terramatrix.io"
                className="hover:underline"
                style={{ color: colors.primary }}
              >
                pilot@terramatrix.io
              </a>
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Form View
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: colors.lightGray }}
    >
      <SEO
        title="Contact TerraMatrix - Get Started with IoT Sensor Analytics"
        description="Contact TerraMatrix to learn how our IoT sensor systems and computer vision analytics can transform your agricultural, brewery, or restaurant operations. Request a demo or speak with our team."
        canonical="/contact"
        keywords="contact terramatrix, IoT consultation, sensor system demo, restaurant analytics inquiry, agriculture monitoring contact"
      />
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3">
        <button
          onClick={handleBackToHome}
          className="flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-opacity"
          style={{ color: colors.primary }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm max-w-lg mx-auto p-6">
          {/* Header */}
          <div className="mb-6">
            <h1
              className="text-2xl font-semibold mb-2"
              style={{ color: colors.charcoal }}
            >
              Join Our Pilot Program
            </h1>
            <p className="text-sm leading-relaxed" style={{ color: colors.midGray }}>
              TerraMatrix is currently in a closed pilot program. We're selecting
              partners who can help shape the future of AI-powered IoT management.
              Tell us about your business and how we can help.
            </p>
          </div>

          {/* Form */}
          <PilotContactForm
            onSuccess={handleSuccess}
            showCancelButton={false}
          />
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 px-4 py-4 text-center">
        <p className="text-xs" style={{ color: colors.midGray }}>
          &copy; {new Date().getFullYear()} TerraMatrix. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default ContactPage;
